import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Grid, Environment, Lightformer } from "@react-three/drei";
import * as THREE from "three";

const rng = (p, a, b) => Math.min(Math.max((p - a) / (b - a), 0), 1);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

const MAT = {
  concrete: { color: "#9a958a", roughness: 0.92 },
  stone: { color: "#6e675b", roughness: 1 },
  wood: { color: "#8a6642", roughness: 0.55 },
  steel: { color: "#1b1b1f", roughness: 0.35, metalness: 0.85 },
  roof: { color: "#26262b", roughness: 0.8 },
};

function Rise({ progress, range, from = 0.6, children, ...props }) {
  const g = useRef();
  useFrame(() => {
    if (!g.current) return;
    const t = easeOut(rng(progress.current, range[0], range[1]));
    g.current.visible = t > 0.002;
    g.current.scale.y = Math.max(t, 0.001);
    g.current.position.y = (props.y || 0) - from * (1 - t);
  });
  const { y, ...rest } = props;
  return (
    <group ref={g} position={[rest.x || 0, y || 0, rest.z || 0]}>
      {children}
    </group>
  );
}

function Fade({ progress, range, children }) {
  const g = useRef();
  useFrame(() => {
    if (!g.current) return;
    const t = rng(progress.current, range[0], range[1]);
    g.current.visible = t > 0.002;
    g.current.traverse((o) => {
      if (o.material) o.material.opacity = o.material.userData.base * t;
    });
  });
  return <group ref={g}>{children}</group>;
}

function Mesh({ size, pos, mat, castShadow = true }) {
  return (
    <mesh position={pos} castShadow={castShadow} receiveShadow>
      <boxGeometry args={size} />
      <meshStandardMaterial {...mat} />
    </mesh>
  );
}

function Glass({ size, pos, mullions = 3 }) {
  return (
    <group position={pos}>
      <mesh>
        <boxGeometry args={size} />
        <meshStandardMaterial
          color="#a8c2cc"
          transparent
          opacity={0}
          roughness={0.08}
          metalness={0.55}
          envMapIntensity={1.6}
          userData={{ base: 0.38 }}
        />
      </mesh>
      {Array.from({ length: mullions }).map((_, i) => (
        <mesh key={i} position={[(i - (mullions - 1) / 2) * (size[0] / mullions), 0, size[2] / 2 + 0.03]}>
          <boxGeometry args={[0.09, size[1], 0.06]} />
          <meshStandardMaterial color="#1b1b1f" roughness={0.35} metalness={0.85} transparent opacity={0} userData={{ base: 1 }} />
        </mesh>
      ))}
    </group>
  );
}

function CameraRig({ progress }) {
  const { pointer } = useThree();
  useFrame(({ camera }) => {
    const p = progress.current;
    window.__p = p;
    const t = easeOut(rng(p, 0.5, 1));
    const angle = THREE.MathUtils.lerp(0.95, 0.55, t) + pointer.x * 0.045;
    const radius = THREE.MathUtils.lerp(26, 15.5, t);
    const height = THREE.MathUtils.lerp(12, 4.6, t) + pointer.y * 0.5;
    camera.position.set(Math.sin(angle) * radius, height, Math.cos(angle) * radius);
    camera.lookAt(0, 2.6, 0);
  });
  return null;
}

function InteriorGlow({ progress }) {
  const light = useRef();
  const light2 = useRef();
  const group = useRef();
  const glowMats = useRef([]);
  useFrame(() => {
    const t = easeOut(rng(progress.current, 0.72, 0.88));
    if (group.current) group.current.visible = t > 0.01;
    if (light.current) light.current.intensity = t * 40;
    if (light2.current) light2.current.intensity = t * 22;
    glowMats.current.forEach((m) => m && (m.emissiveIntensity = t * 2.2));
  });
  const glow = (pos, size, key) => (
    <mesh key={key} position={pos}>
      <boxGeometry args={size} />
      <meshStandardMaterial
        ref={(m) => m && !glowMats.current.includes(m) && glowMats.current.push(m)}
        color="#241b10"
        emissive="#ffb36b"
        emissiveIntensity={0}
      />
    </mesh>
  );
  return (
    <group ref={group} visible={false}>
      <pointLight ref={light} position={[-1.5, 3.4, 0]} color="#ffb36b" intensity={0} distance={18} decay={2} />
      <pointLight ref={light2} position={[5.9, 2, 0]} color="#ffc98a" intensity={0} distance={10} decay={2} />
      {glow([-1.5, 1.9, -2.9], [7.6, 2.4, 0.1], "g1")}
      {glow([-1.5, 5.1, -2.9], [7.6, 2.4, 0.1], "g2")}
      {glow([5.9, 1.6, -2.4], [4.2, 1.8, 0.1], "g3")}
    </group>
  );
}

function BlueprintGrid({ progress }) {
  const g = useRef();
  useFrame(() => {
    if (!g.current) return;
    const fadeIn = rng(progress.current, 0, 0.06);
    const fadeOut = 1 - rng(progress.current, 0.42, 0.6);
    g.current.visible = fadeIn * fadeOut > 0.01;
    g.current.traverse((o) => {
      if (o.material) o.material.opacity = 0.35 * fadeIn * fadeOut;
    });
  });
  return (
    <group ref={g}>
      <Grid
        position={[0, 0.02, 0]}
        args={[60, 60]}
        cellSize={1}
        cellColor="#C5A059"
        sectionSize={5}
        sectionColor="#C5A059"
        fadeDistance={55}
        fadeStrength={1}
        infiniteGrid
        cellThickness={0.6}
        sectionThickness={1}
        transparent
      />
    </group>
  );
}

export default function HouseScene({ progress }) {
  return (
    <group>
      <CameraRig progress={progress} />
      <BlueprintGrid progress={progress} />

      <Environment resolution={64} background={false}>
        <Lightformer intensity={1.4} position={[0, 8, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[16, 16, 1]} color="#fff3e0" />
        <Lightformer intensity={1.1} position={[-8, 3, 6]} rotation={[0, Math.PI / 3, 0]} scale={[10, 4, 1]} color="#ffd9a8" />
        <Lightformer intensity={0.5} position={[8, 4, -6]} rotation={[0, -Math.PI / 3, 0]} scale={[10, 5, 1]} color="#8a93b8" />
      </Environment>

      <ambientLight intensity={0.3} color="#9aa3bd" />
      <directionalLight
        position={[14, 18, 9]}
        intensity={2.1}
        color="#ffd9a8"
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-18}
        shadow-camera-right={18}
        shadow-camera-top={18}
        shadow-camera-bottom={-18}
        shadow-bias={-0.0004}
      />
      <hemisphereLight args={["#333c52", "#141008", 0.4]} />

      <mesh position={[0, -0.05, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[90, 90]} />
        <meshStandardMaterial color="#131311" roughness={1} />
      </mesh>

      {/* 01 Foundation */}
      <Rise progress={progress} range={[0.04, 0.16]} y={0} from={0.8}>
        <Mesh size={[15, 0.5, 11]} pos={[0, 0.25, 0]} mat={MAT.concrete} />
        <Mesh size={[15.6, 0.08, 11.6]} pos={[0, 0.54, 0]} mat={MAT.stone} castShadow={false} />
      </Rise>

      {/* 02 Steel framing */}
      <Rise progress={progress} range={[0.14, 0.3]} y={0.58}>
        {[[-5.2, -3.4], [-5.2, 3.4], [2.2, -3.4], [2.2, 3.4], [-1.5, -3.4], [-1.5, 3.4]].map(([x, z], i) => (
          <Mesh key={i} size={[0.2, 6.4, 0.2]} pos={[x, 3.2, z]} mat={MAT.steel} />
        ))}
        <Mesh size={[7.8, 0.35, 7]} pos={[-1.5, 3.55, 0]} mat={MAT.concrete} />
        {[[4.6, -2.6], [4.6, 2.6], [7.2, -2.6], [7.2, 2.6]].map(([x, z], i) => (
          <Mesh key={`b${i}`} size={[0.2, 3, 0.2]} pos={[x, 1.5, z]} mat={MAT.steel} />
        ))}
      </Rise>

      {/* 03 Exterior walls */}
      <Rise progress={progress} range={[0.28, 0.46]} y={0.58}>
        <Mesh size={[7.8, 6.4, 0.3]} pos={[-1.5, 3.2, -3.35]} mat={MAT.concrete} />
        <Mesh size={[0.3, 6.4, 7]} pos={[-5.25, 3.2, 0]} mat={MAT.stone} />
        <Mesh size={[0.3, 2.9, 7]} pos={[2.25, 5.05, 0]} mat={MAT.concrete} />
        <Mesh size={[4.8, 3, 0.3]} pos={[5.9, 1.5, -2.75]} mat={MAT.stone} />
        <Mesh size={[0.3, 3, 5.2]} pos={[7.35, 1.5, 0]} mat={MAT.stone} />
        <Mesh size={[4.8, 3, 0.3]} pos={[5.9, 1.5, 2.75]} mat={MAT.concrete} />
        <Mesh size={[1.6, 2.9, 0.3]} pos={[1.45, 1.45, 3.35]} mat={MAT.stone} />
        <Mesh size={[1.1, 2.4, 0.14]} pos={[-0.35, 1.2, 3.42]} mat={{ color: "#2e2019", roughness: 0.5 }} />
      </Rise>

      {/* 04 Wood cladding + roof */}
      <Rise progress={progress} range={[0.42, 0.58]} y={0.58}>
        <Mesh size={[4.2, 2.9, 0.18]} pos={[-3.3, 5.05, 3.42]} mat={MAT.wood} />
        {Array.from({ length: 9 }).map((_, i) => (
          <Mesh key={i} size={[0.1, 2.9, 0.06]} pos={[-5.2 + i * 0.47 + 0.1, 5.05, 3.55]} mat={{ color: "#6e5233", roughness: 0.6 }} castShadow={false} />
        ))}
        <Mesh size={[0.18, 2.9, 4]} pos={[2.32, 1.95, 1.4]} mat={MAT.wood} />
        <Mesh size={[7.8, 0.5, 7.4]} pos={[-1.5, 6.85, 0]} mat={MAT.roof} />
        <Mesh size={[8.2, 0.28, 7.8]} pos={[-1.5, 7.05, 0]} mat={MAT.steel} />
        <Mesh size={[5.2, 0.4, 5.8]} pos={[5.9, 3.25, 0]} mat={MAT.roof} />
        <Mesh size={[5.6, 0.24, 6.2]} pos={[5.9, 3.42, 0]} mat={MAT.steel} />
      </Rise>

      {/* 05 Glass */}
      <Fade progress={progress} range={[0.52, 0.68]}>
        <Glass size={[4.6, 2.7, 0.1]} pos={[-0.2, 2.45, 3.42]} mullions={4} />
        <Glass size={[4.6, 2.7, 0.1]} pos={[-0.2, 5.55, 3.42]} mullions={4} />
        <Glass size={[2.2, 2.4, 0.1]} pos={[4.9, 1.95, 2.72]} mullions={2} />
        <Glass size={[0.1, 2.4, 3]} pos={[2.32, 1.95, -1.6]} mullions={1} />
      </Fade>

      {/* 06 Warm interior lights */}
      <InteriorGlow progress={progress} />

      {/* 07 Landscaping */}
      <Rise progress={progress} range={[0.78, 0.92]} y={0} from={0.5}>
        {[[-4, 7.2], [-1, 7.2], [2, 7.2]].map(([x, z], i) => (
          <Mesh key={i} size={[1.6, 0.9, 0.9]} pos={[x, 0.45, z]} mat={{ color: "#2c3823", roughness: 1 }} />
        ))}
        <Mesh size={[2.2, 0.12, 4.4]} pos={[-0.5, 0.06, 6.2]} mat={MAT.concrete} castShadow={false} />
        <Mesh size={[3.4, 0.06, 1.8]} pos={[5.4, 0.03, 5.6]} mat={{ color: "#16303a", roughness: 0.08, metalness: 0.7 }} castShadow={false} />
        {[[-6.5, 6.5], [6.8, 6.8]].map(([x, z], i) => (
          <Mesh key={`p${i}`} size={[0.5, 1.6, 0.5]} pos={[x, 0.8, z]} mat={{ color: "#22291d", roughness: 1 }} />
        ))}
      </Rise>
    </group>
  );
}
